import { useMutation, useQuery } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import {
  ApiError,
  getAlignmentPage,
  getTemplate,
  listTemplates,
  testPrinterProfile,
  type PrinterProfile,
  type PrinterTestResult,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { saveBlob } from "@/components/download";
import { DetailField, EmptyState, ErrorBanner, ToolbarButton } from "@/components/ui";
import { useObjectURL } from "@/components/useObjectURL";

const detailApi = getRouteApi("/staff/printers/$printerId");

const yesNo = (value: boolean) => (value ? "Yes" : "No");

export function TestPrinterPanel({ profile }: { profile: PrinterProfile }) {
  const test = useMutation({ mutationFn: () => testPrinterProfile(profile.id) });
  const status = test.error instanceof ApiError ? test.error.status : null;

  return (
    <section className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Test printer</h2>
          <p className="mt-1 text-xs text-zinc-500">Asks the printer over IPP for its state. Nothing is printed.</p>
        </div>
        <ToolbarButton disabled={!profile.ipp_uri || test.isPending} onClick={() => test.mutate()}>
          {test.isPending ? "Testing..." : "Test printer"}
        </ToolbarButton>
      </div>
      {!profile.ipp_uri || status === 409 ? (
        <p className="text-xs text-zinc-500">
          Add an IPP printer address above to test it. Without one, spools are downloaded and printed by hand.
        </p>
      ) : null}
      <ErrorBanner error={test.error} />
      {status === 422 ? (
        <p className="text-xs text-zinc-500">
          The server only contacts printers it is told to trust: ops must add the printer's host to PRINT_IPP_ALLOWED_HOSTS
          on both the api and the worker.
        </p>
      ) : null}
      {test.data ? <TestResult result={test.data} /> : null}
    </section>
  );
}

function TestResult({ result }: { result: PrinterTestResult }) {
  if (!result.ok || !result.printer) {
    return (
      <p role="status" className="text-sm text-red-700">
        The printer did not answer: {result.error ?? "no details"}
      </p>
    );
  }
  const printer = result.printer;
  return (
    <div role="status" className="space-y-2">
      <p className="text-sm font-medium text-emerald-700">The printer answered.</p>
      <dl className="grid gap-3 text-sm md:grid-cols-3">
        <DetailField label="Name" value={printer.name || "-"} />
        <DetailField label="Make and model" value={printer.make_and_model || "-"} />
        <DetailField label="State" value={printer.state || "Not reported"} />
        <DetailField label="Accepting jobs" value={yesNo(printer.accepting_jobs)} />
        <DetailField label="Takes PDF" value={yesNo(printer.pdf_supported)} />
        <DetailField label="Paper ready" value={printer.paper_ready === null ? "Not reported" : yesNo(printer.paper_ready)} />
      </dl>
      {!printer.pdf_supported ? (
        <p className="text-xs text-red-700">This printer doesn't take PDF, so spools can't be sent to it; print them by hand.</p>
      ) : null}
    </div>
  );
}

const pickerClass = "mt-1 block min-w-64 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";

export function AlignmentPanel({ profile }: { profile: PrinterProfile }) {
  const { can } = useSession();
  return (
    <section className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div>
        <h2 className="text-sm font-semibold">Alignment page</h2>
        <p className="mt-1 text-xs text-zinc-500">
          A template drawn with sample data and this profile's offsets, margins and stock. Print it on the real stock and check
          every field lands where it should; adjust the offsets or margins above and print it again until it does.
        </p>
      </div>
      {can("template.read") ? (
        <AlignmentPicker profile={profile} />
      ) : (
        <EmptyState
          title="No templates to pick from"
          detail="Picking a template needs the template.read permission."
          compact
        />
      )}
    </section>
  );
}

function AlignmentPicker({ profile }: { profile: PrinterProfile }) {
  const search = detailApi.useSearch();
  const navigate = useNavigate();
  const templates = useQuery({ queryKey: ["templates"], queryFn: listTemplates });
  const template = useQuery({
    queryKey: ["template", search.template],
    queryFn: () => getTemplate(search.template!),
    enabled: search.template !== undefined,
  });
  const versionId = search.version;
  const page = useQuery({
    // updated_at re-renders the page once saved offsets or margins change it.
    queryKey: ["alignment-page", profile.id, versionId, profile.updated_at],
    queryFn: () => getAlignmentPage(profile.id, versionId!),
    enabled: versionId !== undefined,
    // Each fetch is a fresh server render, so don't redo it on focus or remount.
    staleTime: Infinity,
  });
  const current = page.isFetching || page.isError ? undefined : page.data;
  const url = useObjectURL(current);
  const version = template.data?.versions.find((v) => v.id === versionId);

  const pick = (next: { template?: string; version?: string }) =>
    void navigate({ to: "/printers/$printerId", params: { printerId: profile.id }, search: next });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">Template</span>
          <select
            value={search.template ?? ""}
            onChange={(e) => {
              const picked = templates.data?.find((t) => t.id === e.target.value);
              pick({ template: picked?.id, version: picked?.current_version?.id });
            }}
            className={pickerClass}
          >
            <option value="">Choose a template</option>
            {(templates.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        {search.template ? (
          <label className="block text-sm">
            <span className="font-medium text-zinc-700">Version</span>
            <select
              value={versionId ?? ""}
              onChange={(e) => pick({ template: search.template, version: e.target.value || undefined })}
              className={pickerClass}
            >
              <option value="">Choose a version</option>
              {(template.data?.versions ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.version_no} ({v.status}, {v.page_size})
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
      <ErrorBanner error={templates.error ?? template.error ?? page.error} />
      {page.isFetching ? <EmptyState title="Rendering alignment page" detail="The server is drawing the PDF." compact /> : null}
      {url && current ? (
        <>
          <ToolbarButton
            onClick={() => {
              const name = template.data?.name ?? "template";
              saveBlob(current, `${profile.name} alignment ${name}${version ? ` v${version.version_no}` : ""}.pdf`);
            }}
          >
            Download alignment page
          </ToolbarButton>
          <iframe title="Alignment page" src={url} className="h-[70vh] w-full rounded-md border border-zinc-200" />
        </>
      ) : null}
    </div>
  );
}
